import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@app/components/ui/card'
import { cn } from '@app/lib/cn'
import {
  dayAbbreviation,
  formatPairs,
  isWeekStart,
  isWeekend,
  shortDate,
  formatDate,
  type DashboardOutputSummary,
} from './api'

/**
 * Daily production output — day-by-day totals of `Urtx_fin_gch` rows.
 *
 * Zero days are not hidden: the factory doesn't produce every day (weekends,
 * line changeovers), and an empty day is itself information. No chart library
 * — every column is a percentage-height div; zero days stop at a thin baseline.
 */

const WINDOWS = [7, 14, 30] as const

export interface OutputBarsProps {
  output: DashboardOutputSummary
  dayCount: number
  onDayCountChange: (days: number) => void
  /** Columns fade but stay in place while a new window loads. */
  refreshing?: boolean
}

export function OutputBars({
  output,
  dayCount,
  onDayCountChange,
  refreshing = false,
}: OutputBarsProps) {
  const peak = output.peakPairs
  const today = output.days.at(-1)?.day
  const denseChart = output.days.length > 14

  return (
    <Card className="flex min-w-0 flex-col">
      <CardHeader>
        <div>
          <CardTitle>Üretim çıktısı</CardTitle>
          <CardDescription>Günlük biten çift.</CardDescription>
        </div>
        <WindowPicker value={dayCount} onChange={onDayCountChange} />
      </CardHeader>

      <CardContent className="flex-1">
        {output.totalPairs === 0 ? (
          <p className="py-6 text-center text-data text-ink-muted">
            Son {dayCount} günde üretimden çıkış kaydı yok.
            {output.lastRecordDay ? ` Son kayıt ${formatDate(output.lastRecordDay)}.` : ''}{' '}
            Daha geniş bir pencere seçin.
          </p>
        ) : (
          <div className={cn('transition-opacity', refreshing && 'opacity-45')}>
            <div className={cn('flex items-end', denseChart ? 'gap-px' : 'gap-1')}>
              {output.days.map((day) => {
                const percent = peak > 0 ? (day.pairs / peak) * 100 : 0
                const isToday = day.day === today
                return (
                  <div key={day.day} className="flex min-w-0 flex-1 flex-col items-center gap-1">
                    {/* Long windows have no room for a number per column; the value stays in the tooltip. */}
                    {!denseChart && (
                      <span
                        className={cn(
                          'font-mono tnum text-micro',
                          day.pairs > 0 ? 'text-ink' : 'text-transparent',
                        )}
                      >
                        {day.pairs > 0 ? formatPairs(day.pairs) : '0'}
                      </span>
                    )}

                    <div
                      className="flex h-24 w-full items-end"
                      title={`${formatDate(day.day)}: ${formatPairs(day.pairs)} çift (${formatPairs(day.records)} kayıt)`}
                    >
                      <div
                        className={cn(
                          'w-full rounded-t-[1px]',
                          day.pairs > 0 ? 'bg-kosele/70' : 'bg-hairline',
                          isToday && day.pairs > 0 && 'bg-kordovan',
                          // On long windows, the first day of the week gets a thin marker.
                          denseChart && isWeekStart(day.day) && 'outline outline-hairline',
                        )}
                        style={{ height: day.pairs > 0 ? `${Math.max(percent, 4)}%` : '2px' }}
                      />
                    </div>

                    {!denseChart && (
                      <>
                        <span
                          className={cn(
                            'text-micro leading-none',
                            isWeekend(day.day) ? 'text-ink-muted/45' : 'text-ink-muted',
                          )}
                        >
                          {dayAbbreviation(day.day)}
                        </span>
                        <span className="font-mono text-[0.625rem] leading-none text-ink-muted/60">
                          {shortDate(day.day)}
                        </span>
                      </>
                    )}
                  </div>
                )
              })}
            </div>

            {/* On dense windows the axis is summarised by its two ends — labels don't overlap. */}
            {denseChart && (
              <div className="mt-1.5 flex items-baseline justify-between border-t border-hairline pt-1 font-mono text-[0.625rem] text-ink-muted">
                <span>{shortDate(output.days[0]?.day ?? '')}</span>
                <span>{shortDate(today ?? '')}</span>
              </div>
            )}
          </div>
        )}
      </CardContent>

      <footer className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-t border-hairline px-3.5 py-2 text-micro text-ink-muted">
        <span>
          Son {dayCount} günde{' '}
          <span className="font-mono tnum font-medium text-ink">{formatPairs(output.totalPairs)}</span> çift.
        </span>
        <span>
          {output.lastRecordDay
            ? `Son çıkış kaydı ${formatDate(output.lastRecordDay)}.`
            : 'Bu pencerede çıkış kaydı yok.'}
        </span>
      </footer>
    </Card>
  )
}

function WindowPicker({
  value,
  onChange,
}: {
  value: number
  onChange: (days: number) => void
}) {
  return (
    <div
      role="group"
      aria-label="Grafik penceresi"
      className="flex shrink-0 overflow-hidden rounded-xs border border-hairline"
    >
      {WINDOWS.map((days) => {
        const active = days === value
        return (
          <button
            key={days}
            type="button"
            onClick={() => onChange(days)}
            aria-pressed={active}
            className={cn(
              'px-2 py-0.5 font-mono text-micro transition-colors not-first:border-l not-first:border-hairline',
              active
                ? 'bg-kordovan/8 font-medium text-kordovan'
                : 'text-ink-muted hover:bg-kagit hover:text-ink',
            )}
          >
            {days}g
          </button>
        )
      })}
    </div>
  )
}
